import * as Sentry from "@sentry/tanstackstart-react";

import { supabase } from "@/integrations/supabase/client";

/**
 * Identity attached to Sentry events. Deliberately excludes anything
 * credential-like (access code, PIN) and any free-text customer data —
 * only stable identifiers plus the account/plan context needed to tell
 * which deployment affected which user.
 */
export type SentryIdentity = {
  userId: string;
  username?: string | null;
  organisationId?: string | null;
  organisationName?: string | null;
  role?: string | null;
  tier?: string | null;
};

const ACCOUNT_TAGS = [
  "organisation_id",
  "organisation_name",
  "org_role",
  "subscription_tier",
] as const;

export function clearSentryIdentity() {
  Sentry.setUser(null);
  for (const tag of ACCOUNT_TAGS) Sentry.setTag(tag, undefined);
}

export function applySentryIdentity(identity: SentryIdentity) {
  Sentry.setUser({
    id: identity.userId,
    ...(identity.username ? { username: identity.username } : {}),
  });
  Sentry.setTag("organisation_id", identity.organisationId ?? undefined);
  Sentry.setTag("organisation_name", identity.organisationName ?? undefined);
  Sentry.setTag("org_role", identity.role ?? undefined);
  Sentry.setTag("subscription_tier", identity.tier ?? undefined);
}

/**
 * Reads the current session and account context, then attaches it to Sentry.
 * Safe to call repeatedly; signs out cleanly when there is no session.
 */
export async function syncSentryIdentity() {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const user = sessionData.session?.user;
    if (!user) {
      clearSentryIdentity();
      return;
    }

    const [profileRes, memberRes, subRes] = await Promise.all([
      supabase.from("profiles").select("display_name").eq("user_id", user.id).maybeSingle(),
      supabase
        .from("organisation_members")
        .select("organisation_id, role, organisations(name)")
        .eq("user_id", user.id)
        .limit(1)
        .maybeSingle(),
      supabase
        .from("subscriptions")
        .select("tier, status, created_at")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
    ]);

    const member = memberRes.data as
      | { organisation_id?: string | null; role?: string | null; organisations?: { name?: string | null } | null }
      | null;

    applySentryIdentity({
      userId: user.id,
      username: profileRes.data?.display_name ?? null,
      organisationId: member?.organisation_id ?? null,
      organisationName: member?.organisations?.name ?? null,
      role: member?.role ?? null,
      tier: subRes.data?.tier ?? null,
    });
  } catch {
    // Never let observability wiring break the app.
  }
}
